import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import { useEffect, useState } from 'react';

import {
  Button,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import { cn } from '@growdash/design-system/utils/helpers.js';
import BookMarkIcon from '@growdash/growintel/assets/icons/bookmark';
import CompareIcon from '@growdash/growintel/assets/icons/compare';
import { GROWINTEL_PATHS } from '@growdash/growintel/constants/GrowintelPaths';
import { Brand } from '@growdash/growintel/generated';
import {
  useGrowintelService,
  useParams,
  useRouter,
  useTabState,
  useWatchlist,
} from '@growdash/growintel/hooks';
import { Loader2 } from 'lucide-react';

type Props = {
  isLoading: boolean;
  brand: Brand | undefined | null;
};

export default function BrandActions({ isLoading, brand }: Props) {
  const router = useRouter();
  const params = useParams();
  const brandId = params?.id ?? '';

  const service = useGrowintelService();

  const { watchlist, isWatchlisted, refetchWatchlist } = useWatchlist();
  const [toggleWatchlistLoading, setToggleWatchlistLoading] = useState(false);

  const [isInWatchlist, setIsInWatchlist] = useState(isWatchlisted(brandId));

  useEffect(() => {
    const watchlisted = params?.id ? isWatchlisted(brandId) : false;
    setIsInWatchlist(watchlisted);
  }, [watchlist, params?.id]);

  const RenderWatchlistButtonContent = () => (
    <>
      {!toggleWatchlistLoading ? (
        <BookMarkIcon
          className={cn('min-w-5 min-h-5', {
            'fill-white': isInWatchlist,
          })}
        />
      ) : (
        <Loader2 className="animate-spin" />
      )}
      <span className="max-sm:hidden">
        {!toggleWatchlistLoading && !isInWatchlist && 'Save'}
        {toggleWatchlistLoading && !isInWatchlist && 'Saving'}

        {!toggleWatchlistLoading && isInWatchlist && 'In your watchlist'}
        {toggleWatchlistLoading && isInWatchlist && 'Removing'}
      </span>
    </>
  );

  const { updateTabState: updateComparePageState } = useTabState('compare');

  const redirectToComparePage = () => {
    updateComparePageState({
      filters: {
        firstBrand: brand,
        secondBrand: null,
      },
    });
    router.replace(`${GROWINTEL_PATHS.COMPARE}?preset=${brandId}`);
  };

  const handleToggleWatchlist = async () => {
    const brandId = brand?.id;
    if (!brandId) return null;

    setToggleWatchlistLoading(true);
    try {
      if (isInWatchlist) {
        await service?.watchlistAPI.removeBrandFromWatchlistWatchlistBrandIdDelete(
          brandId,
        );
        setIsInWatchlist(false);
      } else {
        await service?.watchlistAPI.addBrandToWatchlistWatchlistBrandIdPost(
          brandId,
        );
        setIsInWatchlist(true);
      }
      refetchWatchlist();
    } catch (error) {
      console.error('Error toggling watchlist status', error);
    } finally {
      setToggleWatchlistLoading(false);
    }
  };

  return (
    <div className="flex py-[0.875rem] items-center justify-between max-sm:gap-2">
      {isLoading ? (
        <div key={1} className="animate-fade-in flex items-center gap-2">
          <Skeleton className="w-8 h-8 rounded-[0.25rem]" />
          <Skeleton className="w-64 h-8 rounded-[0.25rem]" />
        </div>
      ) : (
        <div key={2} className="animate-fade-in flex items-center gap-2">
          <GrowintelImage
            src={brand?.brand_image ?? ''}
            type="brand"
            className="w-8 h-8 rounded-[.25rem] shadow-image"
          />
          <Typography className=" text-[1.75rem] font-bold">
            {brand?.brand_name}
          </Typography>
        </div>
      )}
      <div className="flex gap-2">
        {isLoading || !watchlist ? (
          <>
            <Skeleton className="w-48 h-[2.625rem] rounded-lg" />{' '}
            <Skeleton className="w-32 h-[2.625rem] rounded-lg" />
          </>
        ) : (
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={redirectToComparePage}
                  variant="secondary"
                  className={cn(
                    'animate-fade-in flex items-center gap-2 text-tertiary px-3',
                    {
                      hidden: isLoading,
                    },
                  )}
                >
                  <CompareIcon className="min-w-6 min-h-6" />
                  <span className="max-sm:hidden">Compare This Brand</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Compare this brand with other <br />
                brands to have detailed insights
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  disabled={isLoading}
                  onClick={handleToggleWatchlist}
                  variant="outline"
                  className={cn(
                    'animate-fade-in border-support-orange font-medium hover:text- hover:bg-orange-100/25 text-support-orange',
                    {
                      'bg-support-orange text-white hover:bg-orange-800/75':
                        isInWatchlist,
                    },
                  )}
                >
                  {RenderWatchlistButtonContent()}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isInWatchlist
                  ? 'Remove this brand from your watchlist'
                  : 'Add this brand to your watchlist'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
